import {visibleCurrentLadderOffer, type CurrentLadderAnswer} from "./currentLadderOffer";

export interface ReceivedCouponAnswer extends CurrentLadderAnswer {receivedAt: number; transferId: string | null}
export interface CouponRefusal {presentationId: string; at: number}

/** The API binds currency and expiry to a presentation; a locale change cannot replace that record. */
export function retainsCouponAnswer(accountKey: string | null, transferId: string | null, answer: ReceivedCouponAnswer | null): boolean {
	return accountKey !== null && answer?.accountKey === accountKey && answer.transferId === transferId && answer.resolution.status === "offer";
}

/** This screen's own coupon question has been answered, whatever the answer was. */
function answeredForThisSend(accountKey: string | null, transferId: string | null, answer: ReceivedCouponAnswer | null): boolean {
	return accountKey !== null && answer?.accountKey === accountKey && answer.transferId === transferId;
}

/**
 * The account a coupon may be asked for and shown for while a campaign request is in flight.
 *
 * The campaign outranks the coupon in the one slot they share (`peel/peelTagSlot`), so an unanswered
 * campaign request reserves that slot and the coupon waits for it — but only while this screen's own
 * coupon question is still OPEN. Once it has been answered, admission stands for the whole request:
 *
 * - an answer that IS a coupon keeps a ticket on screen (ui#2486). On the instant path the coupon is
 *   issued at link creation and the campaign is not asked about until the recipient's download
 *   completes, so withdrawing it there unmounts a ticket the sender is looking at;
 * - an answer that is a REFUSAL keeps the invite (ui#2492), which is the tag the slot falls back to on
 *   the same send. Withdrawing there discards the settled answer, and the effect this feeds asks again
 *   the moment the campaign is done — a second empty slot on the far side of the request, which is
 *   the flicker ui#2486 removed reappearing one tag lower.
 *
 * Refusing outright once the campaign HOLDS the slot stays with `currentLadderAccountKey`, and nothing
 * here re-ranks the slot.
 */
export function couponAdmissionKey(accountKey: string | null, transferId: string | null,
	answer: ReceivedCouponAnswer | null, campaignLoading: boolean): string | null {
	return !campaignLoading || answeredForThisSend(accountKey, transferId, answer) ? accountKey : null;
}

/** An authorized or in-flight checkout outlives the ticket that opened it. */
export function closeExpiredCouponDialog(countdown: CouponCountdown, busy: boolean, clientSecret: string | null): boolean {
	return countdown.removed && !busy && clientSecret === null;
}

/** Keep a once-visible ticket long enough to explain its expiry, then reserve the empty slot. */
export function couponPresentation(accountKey: string | null, answer: ReceivedCouponAnswer | null, now: number, refusal: CouponRefusal | null) {
	const offer = visibleCurrentLadderOffer(accountKey, answer, answer?.receivedAt ?? now);
	const countdown = couponCountdown(offer?.expiresAt ?? "", now,
		refusal?.presentationId === offer?.presentationId ? refusal?.at : null);
	return {offer: countdown.removed ? null : offer, countdown,
		expired: offer !== null && countdown.expired};
}

/** The server deadline is the only timer origin, including after a refresh, a tucked ticket's reopen or its dialog being reopened. */
export interface CouponCountdown {text: string | null; expired: boolean; removed: boolean}
const EXPIRED_NOTICE_MS = 2_000;
const PRESENTATION_WINDOW_MS = 60 * 60 * 1_000;

export function couponCountdown(expiresAt: string, now: number, refusedAt: number | null = null): CouponCountdown {
	const deadline = Math.min(Date.parse(expiresAt), refusedAt ?? Infinity);
	if (!Number.isFinite(deadline)) return {text: "00:00", expired: true, removed: true};
	if (deadline - now > PRESENTATION_WINDOW_MS) return {text: null, expired: false, removed: false};
	const seconds = Math.max(0, Math.ceil((deadline - now) / 1_000));
	return {
		text: `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`,
		expired: now >= deadline,
		removed: now >= deadline + EXPIRED_NOTICE_MS,
	};
}
